import _conformsTo from "lodash/fp/conforms";
import _isString from "lodash/fp/isString";

/**
 * A value representing a concept from a code system.
 *
 * As you'll notice, this is missing the `system` property used by FHIR. This is done on purpose, as we'll only use
 * value sets and model properties that implicitly knows their code system. However, this does mean that we can't mix
 * concepts from different code systems, which is OK, because we can "duplicate" concepts when needed, and use concept
 * maps if we need to find both.
 */
export interface Coding<T extends string = string> {
	code: T;
	display: string;
}

/** A value that represents a concept, known from the implicit code system. */
export interface KnownCodableConcept<T extends string = string> {
	coding: Coding<T>;
	text?: undefined;
}

/**
 * A value that represents a concept, but not one from the implicit code system.
 *
 * This doesn't mean that we don't know the concept though! Our current use-case for this is to enable the user to add
 * something other than the concepts presented to them at that time.
 */
export interface UnknownCodableConcept {
	coding?: undefined;
	text: string;
}

/**
 * A value that represents a concept, either known or unknown from the implicit code system.
 *
 * It's recommend that we use this type for values that represents concepts, so we can more easily re-use logic and
 * components, and thus maintain our code base. Most usages need both know and unknown ones.
 */
export type CodableConcept = KnownCodableConcept | UnknownCodableConcept;

/** Type guard to help in narrowing down the `CodableConcept` type to `KnownCodableConcept`. */
export function isKnownCodableConcept(value: unknown): value is KnownCodableConcept {
	return _conformsTo({ coding: _conformsTo({ code: _isString, display: _isString }) }, value);
}

/** Type guard to help in narrowing down the `CodableConcept` type to `UnknownCodableConcept`. */
export function isUnknownCodableConcept(value: unknown): value is UnknownCodableConcept {
	return _conformsTo({ text: _isString }, value);
}

/**
 * A value that represents a concept code, known from the implicit code system.
 *
 * Similar to {@link KnownCodableConcept}, but with only the concept code.
 */
export interface KnownCodableConceptCode<T extends string = string> {
	code: T;
	text?: undefined;
}

/**
 * A value that represents a concept, but not one from the implicit code system.
 *
 * Similar to {@link UnknownCodableConcept}, but with only the concept code.
 */
export interface UnknownCodableConceptCode {
	code?: undefined;
	text: string;
}

/**
 * A value that represents a concept, either by known code, or unknown from the implicit code system.
 *
 * Similar to {@link CodableConcept}, but with only the concept code.
 */
export type CodableConceptCode = KnownCodableConceptCode | UnknownCodableConceptCode;

/**
 * Type guard to help in narrowing down the `CodableConceptCode` type to `KnownCodableConceptCode`.
 *
 * Similar to {@link isKnownCodableConcept}, but with only the concept code.
 */
export function isKnownCodableConceptCode(value: unknown): value is KnownCodableConceptCode {
	return _conformsTo({ code: _isString }, value);
}

/**
 * Type guard to help in narrowing down the `CodableConceptCode` type to `UnknownCodableConceptCode`.
 *
 * Similar to {@link isUnknownCodableConcept}, but with only the concept code.
 */
export function isUnknownCodableConceptCode(value: unknown): value is UnknownCodableConceptCode {
	return _conformsTo({ text: _isString }, value);
}

/**
 * Narrows a codable concept into a codable concept code by extracting the code if it's a known concept or keeping the
 * text if it's an unknown concept.
 */
export function narrowToCodableConceptCode(value: CodableConcept): CodableConceptCode {
	return isKnownCodableConcept(value) ? { code: value.coding.code } : value;
}
